import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';

import { Box, Typography } from '@mui/material';

import PageWithLayout from 'src/components/layout/PageWithLayout';
import { SiteRole } from 'src/features/auth';
import { ISiteAccessShortInfo } from 'src/features/site_access';
import { getSiteAccessesShortInfo } from 'src/features/site_access/store/siteAccessThunks';
import { StudyAssignmentModal } from 'src/features/study_assignment/components/StudyAssignmentModal';
import { StudyAssignmentTable } from 'src/features/study_assignment/components/StudyAssignmentTable';
import { StudyUnassignmentModal } from 'src/features/study_assignment/components/StudyUnassignmentModal';
import { IStudyWithAssignment } from 'src/features/study_assignment/domain/types';
import { RootState, AppDispatch } from 'src/store';

const StudyAssignmentsPage: React.FC = () => {
	const dispatch = useDispatch<AppDispatch>();
	const { siteSlug } = useParams<{ siteSlug: string }>();

	const siteDetails = useSelector(
		(state: RootState) => state.site.siteDetails,
	);

	const [siteUsers, setSiteUsers] = useState<ISiteAccessShortInfo[]>([]);
	const [shouldRefreshTable, setShouldRefreshTable] = useState({
		refreshTable: false,
	});
	const [isAssignmentModalOpen, setIsAssignmentModalOpen] = useState(false);

	const [selectedStudies, setSelectedStudies] = useState<
		IStudyWithAssignment[]
	>([]);
	const [studyToUnassign, setStudyToUnassign] =
		useState<IStudyWithAssignment | null>(null);

	const isAdminUser = (['super_admin', 'admin'] as SiteRole[]).some((role) =>
		siteDetails?.roles.includes(role),
	);
	const breadcrumbs = [
		{
			label: siteDetails?.name || 'Site Details',
			href: `/site/${siteSlug}`,
		},
		{ label: 'Study Assignments' },
	];

	const handleStudyAssignmentClick = async (
		studies: IStudyWithAssignment[],
	) => {
		if (!studies || studies.length === 0) return;

		setIsAssignmentModalOpen(true);
		setSelectedStudies(studies);
	};

	useEffect(() => {
		if (!isAdminUser) {
			return;
		}

		dispatch(
			getSiteAccessesShortInfo({
				siteSlug: siteSlug!,
				requestParams: { role: 'annotator' },
				onSuccess: (response) => {
					setSiteUsers(response);
				},
			}),
		);
	}, [siteDetails]);

	return (
		<PageWithLayout breadcrumbs={breadcrumbs}>
			<Box sx={{ p: 0.5 }}>
				<Box
					sx={{
						display: 'flex',
						flexDirection: 'column',
						gap: 3,
						padding: 3,
						backgroundColor: 'white',
						borderRadius: '4px',
						marginTop: '20px',
					}}
				>
					{/* Header Section */}
					<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
						<Typography
							variant='h6'
							sx={{
								fontSize: '20px',
								fontWeight: 500,
								color: 'rgba(0, 0, 0, 0.87)',
							}}
						>
							Study Assignments
						</Typography>
					</Box>

					<StudyAssignmentTable
						siteSlug={siteSlug || ''}
						refreshTable={shouldRefreshTable}
						onStudyAssignmentChange={handleStudyAssignmentClick}
						onUnassignClick={setStudyToUnassign}
					/>
				</Box>

				<StudyAssignmentModal
					open={isAssignmentModalOpen}
					studyList={selectedStudies}
					onClose={() => {
						setIsAssignmentModalOpen(false);
						setSelectedStudies([]);
					}}
					onSubmit={() => {
						setIsAssignmentModalOpen(false);
						setSelectedStudies([]);
						setShouldRefreshTable({ refreshTable: true });
					}}
					siteUsers={siteUsers}
				/>

				{studyToUnassign && (
					<StudyUnassignmentModal
						open={!!studyToUnassign}
						onClose={() => {
							setStudyToUnassign(null);
						}}
						onSubmit={() => {
							setStudyToUnassign(null);
							setShouldRefreshTable({ refreshTable: true });
						}}
						studyToUnassign={studyToUnassign}
					/>
				)}
			</Box>
		</PageWithLayout>
	);
};

export default StudyAssignmentsPage;
